import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from '@/hooks/use-toast';
import { ApiError } from '@/lib/api';
import { showApiError } from '@/lib/errors';
import { getAiAnalyticsBaseUrl } from '@/lib/api/aiAnalyticsUrl';
import { getOrganizationId } from '@/lib/api/organization';
import { normalizeQaPairs } from '@/lib/liveChat/agentKnowledge';

const AI_AGENT_BASE_URL = getAiAnalyticsBaseUrl('api');

// The AI microservice is FastAPI: errors arrive as { detail: "message" } (HTTP
// errors) or { detail: [{ msg }] } (422 validation). Surface the specific message
// so the UI shows e.g. "An AI agent named 'X' already exists" instead of a bare
// "HTTP 409". Falls back to a legacy `error` field, then the status code.
function extractAiAgentError(errorData: any, status: number): string {
  const d = errorData?.detail;
  if (typeof d === 'string' && d.trim()) return d;
  if (Array.isArray(d)) {
    const msg = d.map((e: any) => e?.msg).filter(Boolean).join('; ');
    if (msg) return msg;
  }
  return errorData?.error || `HTTP ${status}`;
}

/**
 * The status vocabulary the microservice accepts on `ai_chat_agent.status`.
 *
 * 🔴 Source of truth for the wire contract. `AGENT_STATUS_VALUES` in
 * `livechat/ai_agent/repositories/ai_agent_repository.py` mirrors it, and the
 * update endpoint rejects anything else with a 400. `AgentStatus` in
 * `components/Ai/aiAgentsChrome.tsx` is an alias of this — do not redeclare it
 * there, or the display union and the API union can drift.
 */
export type AiAgentStatus = 'Active' | 'Draft' | 'Paused';

export interface AiAgent {
  id: string;
  business_account_id: string;
  agent_name: string;
  website_url?: string | null;
  qa_data: any[];
  document_paths: string[];
  document_ids?: string[];
  documents?: Array<{ id: string; document_path: string; document_name?: string }>;
  suggested_buttons: any[];
  suggested_questions: any[];
  connect: boolean;
  connected_channel?: string | null;
  whatsapp_connected: boolean;
  whatsapp_phone_number_id?: string | null;
  whatsapp_phone_display?: string | null;
  whatsapp_flow_id?: string | null;
  whatsapp_flow_name?: string | null;
  /**
   * Phase 2 presentation fields, persisted on `ai_chat_agent`.
   *
   * 🔴 `null` is MEANINGFUL and is not the same as `undefined`:
   *   - `null`      — the column exists and nobody has set it. Fall back to the
   *                   derived value (`deriveKnowledgeAgentStatus` / `…Desc`).
   *   - `undefined` — the field was not in the response at all, i.e. a backend
   *                   that predates Phase 2. Same fallback, so the UI degrades
   *                   safely against an older microservice.
   * Treating either as "Draft" / disabled would restamp every existing agent.
   */
  status?: AiAgentStatus | null;
  enabled?: boolean | null;
  description?: string | null;
  /**
   * Cheap knowledge-presence flags from the list endpoints, which deliberately
   * do NOT return `qa_data` / `documents` (the crawl body is a MEDIUMTEXT).
   * Absent on the detail path, where the real arrays are present instead.
   */
  has_qa?: boolean;
  has_documents?: boolean;
  connected_channels?: string[];
  organization_id?: string | null;
  created_at: string;
  updated_at: string;
}

interface AiAgentsResponse {
  business_account_id: string;
  agents: AiAgent[];
  count: number;
  success: boolean;
}

type AiAgentsLike =
  | AiAgentsResponse
  | { data?: AiAgentsResponse | AiAgent[]; agents?: AiAgent[] }
  | AiAgent[];

interface CreateAiAgentPayload {
  business_account_id: string;
  agent_name: string;
}

interface CreateAiAgentResponse {
  agent: AiAgent;
  success: boolean;
  message?: string;
}

export interface UpdateAiAgentData {
  agent_name?: string;
  website_url?: string | null;
}

export interface UpdateAiAgentResponse {
  agent: AiAgent;
  success: boolean;
  message?: string;
}

export interface DeleteAiAgentResponse {
  success: boolean;
  message?: string;
}

const apiClient = {
  async get<T>(endpoint: string): Promise<{ data: T | null; error: string | null }> {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${AI_AGENT_BASE_URL}${endpoint}`, {
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
        return { data: null, error: extractAiAgentError(errorData, response.status) };
      }

      const data = await response.json();
      return { data: data.data || data, error: null };
    } catch (error) {
      return { data: null, error: error instanceof Error ? error.message : 'Network error' };
    }
  },

  async post<T>(endpoint: string, body: any): Promise<{ data: T | null; error: string | null }> {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${AI_AGENT_BASE_URL}${endpoint}`, {
        method: 'POST',
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
        return { data: null, error: extractAiAgentError(errorData, response.status) };
      }

      const data = await response.json();
      return { data: data.data || data, error: null };
    } catch (error) {
      return { data: null, error: error instanceof Error ? error.message : 'Network error' };
    }
  },

  async postForm<T>(endpoint: string, formData: FormData): Promise<{ data: T | null; error: string | null }> {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${AI_AGENT_BASE_URL}${endpoint}`, {
        method: 'POST',
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
          // Let the browser set Content-Type for multipart/form-data
        },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
        return { data: null, error: extractAiAgentError(errorData, response.status) };
      }

      const data = await response.json();
      return { data: data.data || data, error: null };
    } catch (error) {
      return { data: null, error: error instanceof Error ? error.message : 'Network error' };
    }
  },

  async putForm<T>(endpoint: string, formData: FormData): Promise<{ data: T | null; error: string | null }> {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${AI_AGENT_BASE_URL}${endpoint}`, {
        method: 'PUT',
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
        },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
        return { data: null, error: extractAiAgentError(errorData, response.status) };
      }

      const data = await response.json();
      return { data: data.data || data, error: null };
    } catch (error) {
      return { data: null, error: error instanceof Error ? error.message : 'Network error' };
    }
  },

  async putJson<T>(endpoint: string, body: any): Promise<{ data: T | null; error: string | null }> {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${AI_AGENT_BASE_URL}${endpoint}`, {
        method: 'PUT',
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
        return { data: null, error: extractAiAgentError(errorData, response.status) };
      }

      const data = await response.json();
      return { data: data.data || data, error: null };
    } catch (error) {
      return { data: null, error: error instanceof Error ? error.message : 'Network error' };
    }
  },

  async postFormUrlEncoded<T>(endpoint: string, data: Record<string, any>): Promise<{ data: T | null; error: string | null }> {
    try {
      const token = localStorage.getItem('auth_token');
      const formBody = new URLSearchParams();
      
      // Convert data to URL-encoded format
      Object.entries(data).forEach(([key, value]) => {
        if (Array.isArray(value) || typeof value === 'object') {
          formBody.append(key, JSON.stringify(value));
        } else {
          formBody.append(key, String(value));
        }
      });

      const response = await fetch(`${AI_AGENT_BASE_URL}${endpoint}`, {
        method: 'POST',
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
        return { data: null, error: extractAiAgentError(errorData, response.status) };
      }

      const responseData = await response.json();
      return { data: responseData.data || responseData, error: null };
    } catch (error) {
      return { data: null, error: error instanceof Error ? error.message : 'Network error' };
    }
  },

  async putFormUrlEncoded<T>(endpoint: string, data: Record<string, any>): Promise<{ data: T | null; error: string | null }> {
    try {
      const token = localStorage.getItem('auth_token');
      const formBody = new URLSearchParams();
      
      // Convert data to URL-encoded format
      Object.entries(data).forEach(([key, value]) => {
        if (Array.isArray(value) || typeof value === 'object') {
          formBody.append(key, JSON.stringify(value));
        } else {
          formBody.append(key, String(value));
        }
      });

      const response = await fetch(`${AI_AGENT_BASE_URL}${endpoint}`, {
        method: 'PUT',
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
        return { data: null, error: extractAiAgentError(errorData, response.status) };
      }

      const responseData = await response.json();
      return { data: responseData.data || responseData, error: null };
    } catch (error) {
      return { data: null, error: error instanceof Error ? error.message : 'Network error' };
    }
  },

  async delete<T>(endpoint: string): Promise<{ data: T | null; error: string | null }> {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${AI_AGENT_BASE_URL}${endpoint}`, {
        method: 'DELETE',
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
        return { data: null, error: extractAiAgentError(errorData, response.status) };
      }

      const data = await response.json();
      return { data: data.data || data, error: null };
    } catch (error) {
      return { data: null, error: error instanceof Error ? error.message : 'Network error' };
    }
  },
};

interface AiAgentDetailResponse {
  agent: AiAgent;
  success: boolean;
}

export interface WebsiteProcessResponse {
  agent_id: string;
  website_url: string;
  content: string;
  key_topics: string[];
  suggested_buttons: { label: string; action: string }[];
  suggested_questions: string[];
  crawl_info: {
    pages_crawled: number;
    images_processed: number;
  };
  token_usage: {
    model: string;
    total_tokens: number;
  };
  success: boolean;
}

export interface QaPair {
  id?: string;
  question: string;
  answer: string;
}

const getStoredOrganizationId = (): string | undefined => getOrganizationId() || undefined;

const normalizeAgents = (payload: AiAgentsLike | null | undefined): AiAgent[] => {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray((payload as any).agents)) return (payload as any).agents;
  if (Array.isArray((payload as any).data)) return (payload as any).data;
  if (Array.isArray((payload as any).data?.agents)) return (payload as any).data.agents;
  return [];
};

/**
 * @param options.enabled Opt OUT of fetching entirely. Needed because the
 *   `enabled` expression below falls back to `getStoredOrganizationId()`, so
 *   passing `undefined` for both ids does NOT stop the request — it silently
 *   widens to every agent in the org. Callers that must not read this data at
 *   all (e.g. a role that may not see knowledge agents) have to say so
 *   explicitly; filtering the result client-side would still ship agent names
 *   and ids to that user's browser.
 */
export function useAiAgents(
  businessAccountId?: string,
  organizationId?: string,
  options?: { enabled?: boolean },
) {
  const callerEnabled = options?.enabled !== false;
  return useQuery({
    queryKey: ['ai-agents', businessAccountId, organizationId],
    queryFn: async () => {
      const resolvedOrganizationId = organizationId || getStoredOrganizationId();

      if (!businessAccountId && !resolvedOrganizationId) return [];

      if (businessAccountId) {
        const result = await apiClient.get<AiAgentsLike>(`/ai-agent/business/${businessAccountId}/agents`);
        if (!result.error) {
          return normalizeAgents(result.data);
        }
      }

      if (!resolvedOrganizationId) return [];

      const result = await apiClient.get<AiAgentsLike>(
        `/ai-agent/organization/${encodeURIComponent(resolvedOrganizationId)}/agents`
      );
      if (result.error) {
        throw ApiError.fromResult(result);
      }

      return normalizeAgents(result.data);
    },
    enabled:
      callerEnabled
      && (!!businessAccountId || !!organizationId || !!getStoredOrganizationId()),
  });
}

export function useCreateAiAgent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: CreateAiAgentPayload): Promise<CreateAiAgentResponse> => {
      const formData = new FormData();
      formData.append('business_account_id', payload.business_account_id);
      formData.append('agent_name', payload.agent_name);
      // Send the organization_id too so the backend's duplicate-name check is
      // org-scoped — matching the org-scoped list the user sees. Falls back to
      // business-account scope on the backend when org is unavailable.
      const orgId = getStoredOrganizationId();
      if (orgId) formData.append('organization_id', orgId);

      const result = await apiClient.postForm<CreateAiAgentResponse>('/ai-agent/create-name', formData);

      if (result.error) {
        throw ApiError.fromResult(result);
      }

      if (!result.data) {
        throw new Error('No data returned from API');
      }

      return result.data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['ai-agents', variables.business_account_id] });
      toast({ variant: "success",
        title: 'Agent created successfully',
        description: data.message || `AI Agent "${data.agent.agent_name}" has been created.`,
      });
    },
    onError: (error: Error) => {
      showApiError(error, { fallback: { title: 'Failed to create agent' } });
    },
  });
}

export function useAiAgent(agentId?: string) {
  return useQuery({
    queryKey: ['ai-agent', agentId],
    queryFn: async () => {
      if (!agentId) return null;

      const result = await apiClient.get<AiAgentDetailResponse | AiAgent>(`/ai-agent/${agentId}`);
      if (result.error) {
        throw ApiError.fromResult(result);
      }

      const data = result.data;
      if (!data) return null;

      // Backend may return { agent: {...} } or just the agent object itself
      if ((data as AiAgentDetailResponse).agent) {
        return (data as AiAgentDetailResponse).agent;
      }

      return data as AiAgent;
    },
    enabled: !!agentId,
  });
}

/**
 * Bare request functions for the knowledge endpoints, exported so the CREATE
 * flow can reuse them.
 *
 * 🔴 Why these exist. The hooks below are `useXxx(agentId)` — they close over an
 * agent id at hook-call time, which the create drawer does not have (the agent is
 * created mid-submit, and React forbids calling a hook then). The drawer's first
 * pass therefore hand-rolled its own `fetch` helpers, and **two of them targeted
 * the wrong content type**: `/ai-agent/add-qa` and `/ai-agent/process-website`
 * are FastAPI `Form(...)` endpoints, so a JSON body 422s. Every Q&A pair and
 * reference site typed into the create drawer was silently discarded.
 *
 * So the shape is defined ONCE here, next to the local `apiClient` these routes
 * need, and both the hooks and the drawer call it. Do NOT re-hand-roll a `fetch`
 * for these endpoints — check the `Form(...)` vs pydantic-model signature in
 * `livechat/ai_agent/routes.py` first if you think you need to.
 */
export async function processWebsiteRequest(
  agentId: string,
  websiteUrl: string,
  crawlLimit?: number,
): Promise<WebsiteProcessResponse> {
  // Form-encoded, NOT JSON — `process_website` declares `Form(...)` params.
  const formData = new FormData();
  formData.append('agent_id', agentId);
  formData.append('website_url', websiteUrl);
  formData.append('crawl_limit', String(crawlLimit ?? 5));

  const result = await apiClient.postForm<WebsiteProcessResponse>('/ai-agent/process-website', formData);
  if (result.error || !result.data) {
    throw ApiError.fromResult(result, 'Failed to process website');
  }
  return result.data;
}

export function useProcessWebsite(agentId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: { website_url: string; crawl_limit?: number }) =>
      processWebsiteRequest(agentId, params.website_url, params.crawl_limit),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-agent', agentId] });
      toast({
        title: 'Website processed',
        description: 'The website content has been processed for this agent.',
      });
    },
    onError: (error: Error) => {
      showApiError(error, { fallback: { title: 'Failed to process website' } });
    },
  });
}

export function useUpdateWebsite(agentId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: { website_url: string; crawl_limit?: number }): Promise<WebsiteProcessResponse> => {
      const formData = new FormData();
      formData.append('website_url', params.website_url);
      formData.append('crawl_limit', String(params.crawl_limit ?? 5));

      const result = await apiClient.putForm<WebsiteProcessResponse>(`/ai-agent/${agentId}/website`, formData);
      if (result.error || !result.data) {
        throw ApiError.fromResult(result, 'Failed to update website');
      }
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-agent', agentId] });
      toast({ variant: "success",
        title: 'Website updated',
        description: 'The website for this agent has been updated.',
      });
    },
    onError: (error: Error) => {
      showApiError(error, { fallback: { title: 'Failed to update website' } });
    },
  });
}

export function useDeleteWebsite(agentId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (): Promise<{ success: boolean; message?: string }> => {
      const result = await apiClient.delete<{ success: boolean; message?: string }>(
        `/ai-agent/${agentId}/website`
      );
      if (result.error || !result.data) {
        throw ApiError.fromResult(result, 'Failed to delete website');
      }
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-agent', agentId] });
      toast({ variant: "success",
        title: 'Website deleted',
        description: 'The website has been removed from this agent.',
      });
    },
    onError: (error: Error) => {
      showApiError(error, { fallback: { title: 'Failed to delete website' } });
    },
  });
}

export interface QaUpdateResponse {
  agent: AiAgent;
  success: boolean;
  message?: string;
}

/**
 * 🔴 Form-URL-encoded, and the field is `qa_data` — a JSON *string* of the pair
 * ARRAY, not a JSON body and not `qa_pairs`. `add_qa` declares
 * `agent_id: str = Form(...)` + `qa_data: str = Form(...)`. See the note on
 * `processWebsiteRequest`.
 *
 * 🔴 The array-ness is enforced HERE, by `normalizeQaPairs`, never trusted from
 * the caller. The Q&A tab of the edit drawer passed a single
 * `{ question, answer }` object behind an `as any`; `postFormUrlEncoded`
 * stringifies any object just as happily, so the wire carried
 * `qa_data={"question":…}` and every add from that surface came back
 * `400 {"detail":"Q&A data must be a JSON array"}` — while CREATING an agent
 * worked, because that path passes `qaItems.map(...)`, already an array. Hence
 * the report "Q&A adds on create but not on edit".
 *
 * `JSON.stringify` is explicit rather than left to the serializer's
 * array-or-object branch, so the body stays a JSON array string even if that
 * helper is ever changed.
 */
export async function addQaRequest(
  agentId: string,
  qaData: QaPair[] | QaPair,
): Promise<QaUpdateResponse> {
  if (!agentId || !agentId.trim()) {
    throw new Error('Cannot save Q&A without an agent — save the agent first.');
  }

  // requireAnswer: this endpoint APPENDS, so a pair with no answer is junk.
  const pairs = normalizeQaPairs(qaData, { requireAnswer: true });
  if (pairs.length === 0) {
    // Fail loudly on the client rather than posting `[]`, which the service
    // takes as a no-op and reports as success — the "it said saved but nothing
    // appeared" failure this file already carries scars from.
    throw new Error('Add both a question and an answer before saving.');
  }

  const result = await apiClient.postFormUrlEncoded<QaUpdateResponse>('/ai-agent/add-qa', {
    agent_id: agentId.trim(),
    qa_data: JSON.stringify(pairs),
  });
  if (result.error || !result.data) {
    throw ApiError.fromResult(result, 'Failed to add Q&A');
  }
  return result.data;
}

export function useAddQa(agentId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    // A lone pair is accepted and normalised to a one-element array inside
    // `addQaRequest` — see the 400 "must be a JSON array" note there. Typing it
    // is what lets the call site drop the `as any` that hid the bug.
    mutationFn: (qaData: QaPair[] | QaPair) => addQaRequest(agentId, qaData),
    onSuccess: (data) => {
      // Seed straight from the response so the new row shows without waiting for
      // the refetch — delete/save already do this.
      if (data && data.agent) {
        queryClient.setQueryData(['ai-agent', agentId], data.agent);
      }
      queryClient.invalidateQueries({ queryKey: ['ai-agent', agentId] });
      // 🔴 The card grid derives its knowledge status from `has_qa` on the LIST
      // payload (`deriveKnowledgeAgentStatus`), which the detail-only
      // invalidation never refreshed — a just-taught agent kept reading "no
      // knowledge" until a manual reload.
      queryClient.invalidateQueries({ queryKey: ['ai-agents'] });
      toast({ variant: "success",
        title: 'Q&A added',
        description: 'New Q&A pair has been added for this agent.',
      });
    },
    onError: (error: Error) => {
      showApiError(error, { fallback: { title: 'Failed to add Q&A' } });
    },
  });
}

export function useDeleteQa(agentId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (qaId: string): Promise<QaUpdateResponse> => {
      const result = await apiClient.postFormUrlEncoded<QaUpdateResponse>('/ai-agent/delete-qa', {
        agent_id: agentId,
        qa_id: qaId,
      });
      if (result.error || !result.data) {
        throw ApiError.fromResult(result, 'Failed to delete Q&A');
      }
      return result.data;
    },
    onSuccess: (data) => {
      if (data && data.agent) {
        queryClient.setQueryData(['ai-agent', agentId], data.agent);
      }
      queryClient.invalidateQueries({ queryKey: ['ai-agent', agentId] });
      queryClient.invalidateQueries({ queryKey: ['ai-agents'] });
      toast({ variant: "success",
        title: 'Q&A deleted',
        description: 'Q&A pair has been deleted successfully.',
      });
    },
    onError: (error: Error) => {
      showApiError(error, { fallback: { title: 'Failed to delete Q&A' } });
    },
  });
}

export function useSaveQa(agentId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (qaData: QaPair[] | QaPair): Promise<QaUpdateResponse> => {
      // Backend expects qa_data as form/multipart (see curl example), not pure JSON
      // Equivalent to:
      // curl -X PUT '{BASE_URL}/api/ai-agent/{agentId}/qa' --form 'qa_data="[...]"'
      if (!agentId || !agentId.trim()) {
        // Both current callers guard at the call site, but this path builds a URL
        // from the id — a blank one would PUT to `/ai-agent//qa`. Mirrors the
        // guard in `addQaRequest`.
        throw new Error('Cannot save Q&A without an agent — save the agent first.');
      }

      const token = localStorage.getItem('auth_token');

      /**
       * 🔴 REPLACE semantics, so normalise WITHOUT `requireAnswer`: this request
       * rewrites the entire array, and dropping a stored pair whose answer is
       * blank would silently DELETE it. It also strips `id` — `deriveQaItems`
       * synthesises one for rows that carry none, and both paths already known to
       * work (create drawer, detail page) send `{ question, answer }` only.
       */
      const pairs = normalizeQaPairs(qaData);
      if (pairs.length === 0) {
        // An empty array here would wipe every pair the agent has. "Remove the
        // last one" is delete-qa's job, not this one's.
        throw new Error('Refusing to save an empty Q&A list — delete the pair instead.');
      }

      const formData = new FormData();
      formData.append('qa_data', JSON.stringify(pairs));

      try {
        const response = await fetch(`${AI_AGENT_BASE_URL}/ai-agent/${agentId.trim()}/qa`, {
          method: 'PUT',
          headers: {
            Authorization: token ? `Bearer ${token}` : '',
            // NOTE: Do NOT set Content-Type here; the browser will set the correct multipart boundary
          },
          body: formData,
        });

        const raw = await response.json().catch(() => null);

        if (!response.ok || !raw) {
          throw ApiError.fromResult(raw, `Failed to update Q&A (HTTP ${response.status})`);
        }

        // Normalise to QaUpdateResponse shape
        const data: QaUpdateResponse =
          raw.agent || raw.success !== undefined
            ? {
                agent: (raw.agent ?? raw) as AiAgent,
                success: Boolean(raw.success ?? true),
                message: raw.message,
              }
            : raw;

        if (!data.agent) {
          throw new Error('Invalid response from Q&A update API');
        }

        return data;
      } catch (error) {
        if (error instanceof Error) {
          throw error;
        }
        throw new Error('Failed to update Q&A');
      }
    },
    onSuccess: (data) => {
      // Update the query cache directly with the new data
      if (data && data.agent) {
        queryClient.setQueryData(['ai-agent', agentId], data.agent);
      }
      // Also invalidate to trigger a refetch as backup
      queryClient.invalidateQueries({ queryKey: ['ai-agent', agentId] });
      queryClient.invalidateQueries({ queryKey: ['ai-agents'] });
      toast({ variant: "success",
        title: 'Q&A updated',
        description: 'Q&A pairs have been updated for this agent.',
      });
    },
    onError: (error: Error) => {
      showApiError(error, { fallback: { title: 'Failed to update Q&A' } });
    },
  });
}

export interface AgentQueryResponse {
  agent_id: string;
  query: string;
  response: string;
  /** `automation` | `website` | `document` | `qa` | … — WHICH layer answered. */
  source: string;
  confidence: number;
  success: boolean;
  link_buttons?: Array<{ label: string; url: string }>;
  /**
   * 🔴 Clickable options from an attached automation flow.
   *
   * When an agent has a `whatsapp_flow_id`, the query pipeline answers from that
   * FLOW (`source: "automation"`) and the flow only advances when the caller
   * sends back one of these labels verbatim. Free text re-emits the current node
   * instead — so a caller that ignores this field appears to get "the same
   * response every time" forever. That is exactly the bug the test panel had.
   */
  options?: { items?: string[]; title?: string } | null;
}

export interface AgentQueryPayload {
  agent_id: string;
  query: string;
  business_account_id: string;
}

export interface DocumentsResponse {
  agent_id: string;
  document_ids: string[];
  document_paths: string[];
  success: boolean;
  message?: string;
}

/**
 * @param sessionId Optional stable conversation id. **Pass one for any
 *   multi-turn caller.** Without it the service falls back to keying the
 *   conversation on the caller's IP (`session_id = "__ip__<ip>"`), which is
 *   shared by everyone behind the same NAT and time-windowed to 30 minutes — so
 *   two testers on one office network share, and corrupt, each other's flow
 *   position. The production widget sends its own id plus an `X-Browser-ID`
 *   header for this reason.
 */
export function useAgentQuery(
  agentId: string,
  businessAccountId?: string,
  sessionId?: string,
) {
  return useMutation({
    mutationFn: async (query: string): Promise<AgentQueryResponse> => {
      if (!businessAccountId) {
        throw new Error('Business account ID is required');
      }

      const result = await apiClient.post<AgentQueryResponse>('/ai-agent/query', {
        agent_id: agentId,
        query: query.trim(),
        business_account_id: businessAccountId,
        // Omitted entirely when absent, so existing callers keep the old
        // IP-keyed behaviour rather than sending an empty string (which the
        // service treats as "no session" via `request.session_id or ""`).
        ...(sessionId ? { session_id: sessionId } : {}),
      });

      if (result.error || !result.data) {
        throw ApiError.fromResult(result, 'Failed to query agent');
      }

      return result.data;
    },
    onError: (error: Error) => {
      showApiError(error, { fallback: { title: 'Failed to query agent' } });
    },
  });
}

/** Multipart — `agent_id` as a form field, one `documents` entry per file. */
export async function addDocumentsRequest(
  agentId: string,
  files: File[],
): Promise<DocumentsResponse> {
  const formData = new FormData();
  formData.append('agent_id', agentId);
  files.forEach((file) => {
    formData.append('documents', file);
  });

  const result = await apiClient.postForm<DocumentsResponse>('/ai-agent/add-documents', formData);
  if (result.error || !result.data) {
    throw ApiError.fromResult(result, 'Failed to add documents');
  }

  return result.data;
}

export function useAddDocuments(agentId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (files: File[]) => addDocumentsRequest(agentId, files),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-agent', agentId] });
      toast({ variant: "success",
        title: 'Documents added',
        description: 'Documents have been uploaded for this agent.',
      });
    },
    onError: (error: Error) => {
      showApiError(error, { fallback: { title: 'Failed to add documents' } });
    },
  });
}

export function useUpdateDocuments(agentId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (files: File[]): Promise<DocumentsResponse> => {
      const formData = new FormData();
      files.forEach((file) => {
        formData.append('documents', file);
      });

      const result = await apiClient.putForm<DocumentsResponse>(`/ai-agent/${agentId}/documents`, formData);
      if (result.error || !result.data) {
        throw ApiError.fromResult(result, 'Failed to update documents');
      }

      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-agent', agentId] });
      toast({ variant: "success",
        title: 'Documents updated',
        description: 'Documents have been updated for this agent.',
      });
    },
    onError: (error: Error) => {
      showApiError(error, { fallback: { title: 'Failed to update documents' } });
    },
  });
}

export interface DeleteDocumentResponse {
  agent_id: string;
  document_id: string;
  success: boolean;
  message?: string;
}

export function useDeleteDocument(agentId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (documentId: string): Promise<DeleteDocumentResponse> => {
      const result = await apiClient.delete<DeleteDocumentResponse>(
        `/ai-agent/${agentId}/documents/${encodeURIComponent(documentId)}`
      );
      if (result.error || !result.data) {
        throw ApiError.fromResult(result, 'Failed to delete document');
      }
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-agent', agentId] });
      toast({ variant: "success",
        title: 'Document deleted',
        description: 'The document has been removed from this agent.',
      });
    },
    onError: (error: Error) => {
      showApiError(error, { fallback: { title: 'Failed to delete document' } });
    },
  });
}

export interface ConnectAgentResponse {
  agent_id: string;
  channel_id: string;
  connected: boolean;
  success: boolean;
  message: string;
}

export interface DisconnectAgentResponse {
  agent_id: string;
  disconnected: boolean;
  success: boolean;
  message: string;
}

/**
 * JSON body here — unlike the knowledge endpoints above, `connect_channel` takes
 * a pydantic `ConnectChannelRequest`, so this one genuinely is `POST` + JSON.
 * The difference per endpoint is exactly why these live in one place.
 */
export async function connectAgentRequest(
  agentId: string,
  channelId: string,
): Promise<ConnectAgentResponse> {
  const result = await apiClient.post<ConnectAgentResponse>('/ai-agent/connect', {
    agent_id: agentId,
    channel_id: channelId,
  });

  if (result.error || !result.data) {
    throw ApiError.fromResult(result, 'Failed to connect agent');
  }

  return result.data;
}

export function useConnectAgent(agentId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (channelId: string) => connectAgentRequest(agentId, channelId),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['ai-agent', agentId] });
      queryClient.invalidateQueries({ queryKey: ['live-chat-scripts'] });
      // 🔴 The LIST too, not just the detail row. The unified workspace derives
      // the Live Chat / Automation badges AND the whole D3 agent+channel card
      // merge from `agent.connected_channel` on the LIST payload — so without
      // this the grid kept showing the channel as a separate orphan card and the
      // agent as "No Live Chat" until a manual refresh. Harmless on the detail
      // page, which does not read the list.
      queryClient.invalidateQueries({ queryKey: ['ai-agents'] });
      toast({ variant: "success",
        title: 'Agent connected',
        description: data.message || 'Agent has been connected to the channel successfully.',
      });
    },
    onError: (error: Error) => {
      showApiError(error, { fallback: { title: 'Failed to connect agent' } });
    },
  });
}

export function useDisconnectAgent(agentId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (): Promise<DisconnectAgentResponse> => {
      const result = await apiClient.post<DisconnectAgentResponse>('/ai-agent/disconnect', {
        agent_id: agentId,
      });

      if (result.error || !result.data) {
        throw ApiError.fromResult(result, 'Failed to disconnect agent');
      }

      return result.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['ai-agent', agentId] });
      queryClient.invalidateQueries({ queryKey: ['live-chat-scripts'] });
      // See the note in `useConnectAgent` — the workspace grid reads the list.
      queryClient.invalidateQueries({ queryKey: ['ai-agents'] });
      toast({ variant: "success",
        title: 'Agent disconnected',
        description: data.message || 'Agent has been disconnected from the channel successfully.',
      });
    },
    onError: (error: Error) => {
      showApiError(error, { fallback: { title: 'Failed to disconnect agent' } });
    },
  });
}

// WhatsApp Connect/Disconnect

export interface ConnectWhatsAppResponse {
  agent_id: string;
  phone_number_id: string;
  phone_display?: string;
  connected: boolean;
  success: boolean;
  message: string;
}

export interface DisconnectWhatsAppResponse {
  agent_id: string;
  disconnected: boolean;
  success: boolean;
  message: string;
}

export function useConnectWhatsApp(agentId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: { flow_id: string; flow_name: string; phone_number_id?: string; phone_display?: string }): Promise<ConnectWhatsAppResponse> => {
      const result = await apiClient.post<ConnectWhatsAppResponse>('/ai-agent/connect-whatsapp', {
        agent_id: agentId,
        flow_id: params.flow_id,
        flow_name: params.flow_name,
        phone_number_id: params.phone_number_id || '',
        phone_display: params.phone_display || '',
      });

      if (result.error || !result.data) {
        throw ApiError.fromResult(result, 'Failed to connect WhatsApp');
      }

      return result.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['ai-agent', agentId] });
      // The workspace card's "Automation" badge comes from `whatsapp_connected`
      // on the LIST payload — see the note in `useConnectAgent`.
      queryClient.invalidateQueries({ queryKey: ['ai-agents'] });
      toast({ variant: "success",
        title: 'WhatsApp connected',
        description: data.message || 'Agent has been connected to WhatsApp successfully.',
      });
    },
    onError: (error: Error) => {
      showApiError(error, { fallback: { title: 'Failed to connect WhatsApp' } });
    },
  });
}

export function useDisconnectWhatsApp(agentId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (): Promise<DisconnectWhatsAppResponse> => {
      const result = await apiClient.post<DisconnectWhatsAppResponse>('/ai-agent/disconnect-whatsapp', {
        agent_id: agentId,
      });

      if (result.error || !result.data) {
        throw ApiError.fromResult(result, 'Failed to disconnect WhatsApp');
      }

      return result.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['ai-agent', agentId] });
      // See the note in `useConnectAgent` — the workspace grid reads the list.
      queryClient.invalidateQueries({ queryKey: ['ai-agents'] });
      toast({ variant: "success",
        title: 'WhatsApp disconnected',
        description: data.message || 'Agent has been disconnected from WhatsApp.',
      });
    },
    onError: (error: Error) => {
      showApiError(error, { fallback: { title: 'Failed to disconnect WhatsApp' } });
    },
  });
}

// Edit AI Agent Hook
export function useUpdateAiAgent(agentId?: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: UpdateAiAgentData): Promise<UpdateAiAgentResponse> => {
      if (!agentId) {
        throw new Error('Agent ID is required');
      }

      const formData = new FormData();
      
      if (data.agent_name) formData.append('agent_name', data.agent_name);
      if (data.website_url !== undefined) formData.append('website_url', data.website_url ?? '');

      const result = await apiClient.putForm<UpdateAiAgentResponse>(`/ai-agent/${agentId}/update`, formData);
      
      if (result.error) {
        throw ApiError.fromResult(result);
      }

      if (!result.data) {
        throw new Error('No data returned from API');
      }

      return result.data;
    },
    onSuccess: (data) => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: ['ai-agent', agentId] });
      queryClient.invalidateQueries({ queryKey: ['ai-agents'] });
      
      toast({ variant: "success",
        title: 'Agent updated',
        description: 'AI agent has been updated successfully.',
      });
    },
    onError: (error: Error) => {
      showApiError(error, { fallback: { title: 'Update failed' } });
    },
  });
}

export interface AgentPresentationPatch {
  /** `null` resets to the derived status. Omit to leave unchanged. */
  status?: AiAgentStatus | null;
  enabled?: boolean;
  /** `null` (or an empty string) clears it. Omit to leave unchanged. */
  description?: string | null;
}

/**
 * Persist the Phase-2 presentation fields (`status` / `enabled` / `description`).
 *
 * Separate from `useUpdateAiAgent` on purpose: that hook is the *content* editor
 * (name, website, Q&A, documents) and its success toast says "Agent updated",
 * which is wrong noise for flipping a pill in a list. This one is silent on
 * success — the optimistic pill IS the feedback — and only speaks up on failure.
 *
 * 🔴 Every field is sent CONDITIONALLY. The endpoint reads absence as "leave the
 * column alone", so appending a key with an empty value would clear a field the
 * caller never mentioned. In particular `enabled` is typed `Optional[bool]`
 * server-side: appending `''` for it is not a no-op, it is an unparseable bool.
 *
 * 🔴 Clearing uses the explicit `clear_*` flags, NOT an empty string. FastAPI
 * delivers a submitted empty value as `None`, indistinguishable from an omitted
 * field, so `status=''` would silently leave the old status in place.
 */
export function useUpdateAgentPresentation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      agentId,
      patch,
    }: {
      agentId: string;
      patch: AgentPresentationPatch;
    }): Promise<UpdateAiAgentResponse> => {
      if (!agentId) throw new Error('Agent ID is required');

      const formData = new FormData();

      if (patch.status !== undefined) {
        if (patch.status === null) {
          formData.append('clear_status', 'true');
        } else {
          formData.append('status', patch.status);
        }
      }

      if (patch.enabled !== undefined) {
        formData.append('enabled', patch.enabled ? 'true' : 'false');
      }

      if (patch.description !== undefined) {
        const trimmed = (patch.description ?? '').trim();
        if (trimmed) {
          formData.append('description', trimmed);
        } else {
          formData.append('clear_description', 'true');
        }
      }

      // Nothing to send — don't spend a request (and don't let the endpoint's
      // other defaults, e.g. crawl_limit, touch the agent for no reason).
      if (![...formData.keys()].length) {
        throw new Error('No presentation changes to save');
      }

      const result = await apiClient.putForm<UpdateAiAgentResponse>(
        `/ai-agent/${agentId}/update`,
        formData,
      );

      if (result.error) throw ApiError.fromResult(result);
      if (!result.data) throw new Error('No data returned from API');
      return result.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['ai-agent', variables.agentId] });
      queryClient.invalidateQueries({ queryKey: ['ai-agents'] });
    },
    onError: (error: Error) => {
      showApiError(error, { fallback: { title: 'Could not save the change' } });
    },
  });
}

// Delete AI Agent Hook
export function useDeleteAiAgent(agentId?: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (): Promise<DeleteAiAgentResponse> => {
      if (!agentId) {
        throw new Error('Agent ID is required');
      }

      const result = await apiClient.delete<DeleteAiAgentResponse>(`/ai-agent/${agentId}/delete`);
      
      if (result.error) {
        throw ApiError.fromResult(result);
      }

      if (!result.data) {
        throw new Error('No data returned from API');
      }

      return result.data;
    },
    onSuccess: (data) => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: ['ai-agent', agentId] });
      queryClient.invalidateQueries({ queryKey: ['ai-agents'] });
      
      toast({ variant: "success",
        title: 'Agent deleted',
        description: 'AI agent has been deleted successfully.',
      });
    },
    onError: (error: Error) => {
      showApiError(error, { fallback: { title: 'Delete failed' } });
    },
  });
}
